import { DateTime } from 'luxon';
import moment from 'moment-timezone';

import LogService from './log-service';

// Without the below argument the moment.tz(timeZone) call will "convert" the
// dateTime object to the supplied time zone by adjusting the MM/DD/YYYY HH:MM:SS values.
// Here we want to maintain these values and update only the associated time zone.
const shouldCoerceTimeZoneChangeButNotAdjustDateOrTimeValues = true;

export const convertDateToMomentDateObjAtMidnightInSellerTimeZone = (dateValue, sellerTimeZone) => {
  const momentObj = moment(dateValue);

  if (!momentObj.isValid()) {
    LogService.logError(`Invalid date value: ${dateValue} passed to datetime-service.js.`);
    // return a non-useful value so that the caller errors out instead of operating on an incorrect result — i.e. Date.now()
    return null;
  }

  const momentObjInSellerTimeZone = momentObj.clone().tz(sellerTimeZone, shouldCoerceTimeZoneChangeButNotAdjustDateOrTimeValues);
  const momentObjAtStartOfDayInSellerTimeZone = momentObjInSellerTimeZone.clone().startOf('day');

  return momentObjAtStartOfDayInSellerTimeZone;
};

export const getDayOfMonth = (utcString) => {
  const dateTime = DateTime.fromISO(utcString);
  const { day } = dateTime;
  return day;
};

export const addPeriodOfTime = (utcString, { period, value }) => {
  const adjustedDateTime = DateTime.fromISO(utcString).plus({
    [period]: value,
  });
  return adjustedDateTime;
};

export const getCurrentDateTime = () => DateTime.now();

export const getCurrentDateTimeAsISO = () => {
  return getCurrentDateTime().toISO();
};

export const getCurrentDateTimeAsUtcIso = () => {
  return getCurrentDateTime().toUTC().toISO();
};

export const getDaysBetweenISODates = (startDate, endDate) => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  return end.diff(start, 'days').toObject().days;
};

export const getDaysFromCurrentDate = (utcString) => {
  const startDate = getCurrentDateTime().startOf('day').toISO();
  const endDate = utcString;

  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate).startOf('day');

  return end.diff(start, ['days', 'hours']).toObject().days;
};

export const isMidnight = (datetime) => {
  return datetime.hour() === 0 && datetime.minute() === 0;
};

export const getTotalMinutes = (datetime) => {
  return datetime.hour * 60 + datetime.minute;
};

/**
 * Parses a date string and returns it as an ISO string with time set to UTC noon (12:00:00.000).
 * Use this for date-only values where you want to store a consistent time component that won't
 * cause display issues across timezones.
 * @param {string} date - The date string to parse
 * @param {string} dateFormat - The Luxon date format string for parsing the date
 * @returns {string} The ISO date-time string at UTC noon, or empty string if invalid or not provided
 */
export const getDateAsUtcNoonInISO = (date, dateFormat) => {
  try {
    if (date && dateFormat && typeof date === 'string') {
      const parsedDate = DateTime.fromFormat(date, dateFormat, {
        zone: 'utc',
        setZone: true,
      });
      if (parsedDate.isValid) {
        const dateISO = parsedDate
          .set({
            hour: 12,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .toISO();
        return dateISO || '';
      }
    }
    return '';
  } catch (error) {
    LogService.logError(`Error parsing date in getDateAsUtcNoonInISO: date=${date}, dateFormat=${dateFormat}`, error);
    return '';
  }
};
